/**
 * Bug reports + feature requests — the bits both packages need.
 *
 * Reports are written by BugReportModal and managed on /home/bugs
 * (BugReportsPage). The Claude connector (functions/src/mcp/bug-reports.ts)
 * reads and updates the same `bug_reports` docs, so anything that decides
 * what a write looks like lives here once:
 *
 *   - the status-change patch — moving a report OUT of 'done' must blank
 *     every resolution field, or a reopened report keeps showing a stale
 *     "How it was fixed" card and a later close inherits the old fixer;
 *   - the "your report was fixed / shipped" notification the reporter reads
 *     in their bell, and who gets it;
 *   - the /home/bugs deep link both sides hand out.
 *
 * Performing the write stays per-package: the client uses the web SDK, the
 * server the Admin SDK.
 */

export type BugReportType = 'bug' | 'feature';

export type BugReportStatus = 'new' | 'reviewed' | 'in_progress' | 'done' | 'wont_fix';

/** Every status, in the order /home/bugs lists them. */
export const BUG_REPORT_STATUSES: readonly BugReportStatus[] = [
  'new',
  'reviewed',
  'in_progress',
  'done',
  'wont_fix',
];

/** Statuses still waiting on someone — everything except done / won't fix. */
export const OPEN_BUG_REPORT_STATUSES: readonly BugReportStatus[] = [
  'new',
  'reviewed',
  'in_progress',
];

/**
 * Read a stored status. Missing or unrecognised reads as 'new' — the same
 * fallback the /home/bugs badge uses (its first status option).
 */
export function normalizeBugReportStatus(raw: unknown): BugReportStatus {
  return typeof raw === 'string' && (BUG_REPORT_STATUSES as readonly string[]).includes(raw)
    ? (raw as BugReportStatus)
    : 'new';
}

/** Read a stored type. BugReportModal only writes these two; default to bug. */
export function normalizeBugReportType(raw: unknown): BugReportType {
  return raw === 'feature' ? 'feature' : 'bug';
}

export function isOpenBugReportStatus(status: BugReportStatus): boolean {
  return OPEN_BUG_REPORT_STATUSES.includes(status);
}

/** Who closed a report, when, and what they said about it. */
export interface BugReportResolution {
  /** The fixer's plain-English blurb. Optional in the app, so may be ''. */
  message: string;
  /** ISO timestamp. */
  at: string;
  byId: string;
  byName: string;
}

export type BugReportStatusChange =
  | { to: 'done'; resolution: BugReportResolution }
  | { to: Exclude<BugReportStatus, 'done'> };

export interface BugReportStatusPatch {
  status: BugReportStatus;
  resolutionMessage?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedByName?: string;
  /**
   * Set (to null) when a human puts a report back on 'new'. The autobuild
   * intake skips a report it picked in the last 6 hours (the loop guard for a
   * crashed run); clearing the stamp here is what lets "set it back to open"
   * mean "try again". Dotted paths — the client updateDoc and the Admin SDK
   * both write them as nested fields.
   */
  'autobuild.pickedAt'?: null;
  'autobuild.pickedRun'?: null;
}

/**
 * The fields to write for a status change. `updatedAt` is left to the
 * writer, which stamps it on every update anyway.
 *
 *   - INTO 'done': the status plus the resolution stamp.
 *   - OUT OF 'done': the status plus every resolution field blanked.
 *   - Anything else: just the status.
 *
 * Blanks are '' rather than deleted fields — what the page has always
 * written, and it only renders the resolution card for a non-empty message.
 */
export function bugReportStatusPatch(
  from: BugReportStatus,
  change: BugReportStatusChange,
): BugReportStatusPatch {
  if (change.to === 'done') {
    const { resolution } = change;
    return {
      status: 'done',
      resolutionMessage: resolution.message.trim(),
      resolvedAt: resolution.at,
      resolvedBy: resolution.byId,
      resolvedByName: resolution.byName,
    };
  }
  // Back to 'new' from anywhere else = "run it again": drop the pick stamp.
  const reopen: Pick<BugReportStatusPatch, 'autobuild.pickedAt' | 'autobuild.pickedRun'> =
    change.to === 'new' && from !== 'new' ? { 'autobuild.pickedAt': null, 'autobuild.pickedRun': null } : {};
  if (from === 'done') {
    return {
      status: change.to,
      resolutionMessage: '',
      resolvedAt: '',
      resolvedBy: '',
      resolvedByName: '',
      ...reopen,
    };
  }
  return { status: change.to, ...reopen };
}

/**
 * Whether closing a report should notify its reporter: only when there IS a
 * reporter, and they aren't the person closing it (nobody needs a bell for
 * ticking off their own report).
 */
export function shouldNotifyBugReporter(
  reportedBy: string | undefined,
  closedById: string,
): boolean {
  return !!reportedBy && reportedBy !== closedById;
}

/**
 * The copy for the reporter's "done" notification. The fixer's blurb is the
 * body when there is one, so the reporter sees what was actually done;
 * otherwise a generic line.
 */
export function bugReportDoneNotice(
  report: { type?: string; title?: string },
  resolutionMessage?: string,
): { entityTitle: string; noteContent: string; noteExcerpt: string } {
  const isFeature = report.type === 'feature';
  const label = isFeature ? 'Feature request completed' : 'Bug fixed';
  const verb = isFeature ? 'shipped' : 'fixed';
  const title = (report.title || '').slice(0, 80);

  const blurb = resolutionMessage?.trim();
  const noteContent = blurb
    ? blurb
    : `Your ${isFeature ? 'feature request' : 'bug report'} "${title}" has been ${verb}.`;
  const noteExcerpt = noteContent.length > 140 ? noteContent.slice(0, 140) + '...' : noteContent;

  return { entityTitle: `${label}: ${title}`, noteContent, noteExcerpt };
}

/** The /home/bugs route, opened on one report when an id is given. */
export function bugReportPath(reportId?: string): string {
  return reportId ? `/home/bugs?openId=${encodeURIComponent(reportId)}` : '/home/bugs';
}

/**
 * The inverse: the report id a `/home/bugs?openId=…` path points at, or null
 * for any other path. Plate tasks the pipeline (or tag_team_member) creates
 * carry that path as `sourcePath`; this is how the task knows which report
 * it is about.
 */
export function bugReportIdFromPath(path: string | undefined | null): string | null {
  if (!path) return null;
  const match = /^\/home\/bugs\?(?:.*&)?openId=([^&#]+)/.exec(path);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]) || null;
  } catch {
    return null;
  }
}
